import { Toggle } from '@designsforhealth/dfh-react-components'
import { CircularProgress } from '@material-ui/core'
import React, { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'

import { PutCustomerSettingsPractitionerDocument } from '../../../../../graphql/camel'
import useCamelMutation from '../../../../../hooks/useCamelMutation'
import useSubmitFormOnDirty from '../../../../../hooks/useSubmitFormOnDirty'
import { PractitionerSettings } from '../../../../../lib/practitioner-settings/types'
import { logAndCaptureException } from '../../../../../utils/errorTools'
import { checkAndDiscardQueryResponse } from '../../../../../utils/graphqlTools'
import { MutationErrorAlert } from '../../../../MutationErrorAlert'
import { ConfirmationModal } from './ConfirmationModal'
import { ToggleProgress } from './ToggleProgress'

export interface CommissionsProgramProps {
  children: ReactNode
  settings: PractitionerSettings
}

const StyledContainer = styled.div`
  p {
    font-size: 14px;
    line-height: 16px;
  }
`

const CommissionsProgramToggle = styled(Toggle)`
  && {
    max-width: 350px;
    input:checked + label .slider {
      background-color: ${({ theme }) => theme.colors.royalBlue.shade500};
    }
    label > span {
      display: flex;
      align-items: center;
      // increase width
      flex: 1;
    }
  }
`

interface FormValues {
  earnCommissions: boolean
}

const CommissionsProgram: FC<CommissionsProgramProps> = ({ children, settings }) => {
  const alertRef = useRef<HTMLDivElement>(null)
  const [showConfirmation, setShowConfirmation] = useState(false)

  const [putCustomerSettingsPractitioner] = useCamelMutation(
    PutCustomerSettingsPractitionerDocument,
  )

  const { control, errors, formState, handleSubmit, register, reset, setError, watch } =
    useForm<FormValues>({ mode: 'onChange' })

  const onSubmit = useCallback(
    async ({ earnCommissions }: FormValues) => {
      const result = await putCustomerSettingsPractitioner({
        variables: {
          customerSettingsPractitionerUpdateInput: {
            optedOutOfCommissions: !earnCommissions,
          },
        },
      })
        .then(checkAndDiscardQueryResponse)
        .then(() => ({ error: undefined }))
        .catch((err) => {
          const error = err instanceof Error ? err : new Error(`${err}`)
          logAndCaptureException(error)
          return { error }
        })
      if (result.error) {
        setError('earnCommissions', { message: result.error.message, type: 'server' })
      }
    },
    [putCustomerSettingsPractitioner, setError],
  )

  const { resetForm } = useSubmitFormOnDirty({
    defaultValues: useMemo(
      () => ({
        earnCommissions: !settings.practitioner.commissionsOptOutFormReceived,
      }),
      [settings.practitioner.commissionsOptOutFormReceived],
    ),
    formState,
    handleSubmit,
    onSubmit,
    reset,
    // submit automatically (without confirmation) when enabling commissions program
    submitOnDirty: !!settings.practitioner.commissionsOptOutFormReceived,
  })
  const currentEarnCommissions = watch('earnCommissions')

  useEffect(() => {
    if (formState.isSubmitting) {
      return
    }
    if (!currentEarnCommissions && formState.isDirty) {
      setShowConfirmation(true)
    } else {
      // close modal when no longer needed
      setShowConfirmation(false)
    }
  }, [currentEarnCommissions, formState.isDirty, formState.isSubmitting])

  const resetAndHideModal = () => {
    setShowConfirmation(false)
    resetForm()
  }

  useEffect(() => {
    if (alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [])

  const hasServerError = !!errors.earnCommissions && errors.earnCommissions.type === 'server'

  return (
    <StyledContainer>
      <form onSubmit={handleSubmit(onSubmit)}>
        {hasServerError && !showConfirmation && (
          <div ref={alertRef}>
            <MutationErrorAlert onCancel={resetForm} onRetry={handleSubmit(onSubmit)}>
              An error occurred updating your settings, and we have been notified.
            </MutationErrorAlert>
          </div>
        )}
        <CommissionsProgramToggle
          disabled={formState.isSubmitting || hasServerError}
          id="earnCommissions"
          name="earnCommissions"
          register={register}
          control={control}
          defaultChecked={!settings.practitioner.commissionsOptOutFormReceived}
        >
          {children}
          <ToggleProgress isSubmitting={formState.isSubmitting} />
        </CommissionsProgramToggle>
        <ConfirmationModal
          confirmButtonText={
            <>{formState.isSubmitting && <CircularProgress size={16} />} Yes, turn off</>
          }
          disabled={formState.isSubmitting}
          isOpen={showConfirmation}
          title="Are you sure you want to unenroll from Commissions?"
          description="Your account will no longer earn you Commissions for any current or future months transactions."
          onCancel={resetAndHideModal}
          onConfirm={handleSubmit(onSubmit)}
        >
          {hasServerError && showConfirmation && (
            <MutationErrorAlert onCancel={resetForm} onRetry={handleSubmit(onSubmit)}>
              An error occurred updating your settings, and we have been notified.
            </MutationErrorAlert>
          )}
        </ConfirmationModal>
      </form>
    </StyledContainer>
  )
}

export default CommissionsProgram
